// Modules
import { useEffect, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'

import { useRemoteConfig } from 'modules/RemoteConfig/hooks/useRemoteConfig'

// Application
import { tracking } from 'constants/tracking'
import { useRedirectTo } from 'hooks/useRedirectTo'
import { isTokenExpired, singleSPA } from 'utils'
import { useLoginContext } from 'context'
import { useLoginService } from 'services/useLoginService'
import { useLoginSuccess } from 'hooks/useLoginSuccess'
import { SESSION_STORAGES_KEYS } from 'constants/sessionStorages'
import { useRedirectExternalUniqueToken } from 'hooks/useRedirectExternalUniqueToken'
import { SignInCIPCredentials, SignInCredentials, LoginType } from 'typings/auth'
import { useUserService } from 'services/user'
import { ROUTES_PATH } from 'constants/routesPath'
import { defaultConfig } from 'modules/RemoteConfig/defaultConfig'
import { User, ViewerFeatureFlags } from 'typings/user'
import { goToPage } from 'redesign/utils/goToPage'
import { useTracking } from 'react-tracking'
import { EMPTY_DATA_USER } from 'constants/singin'
import { removePath } from 'redesign/utils/removePath'

type UserType = 'FULL' | 'LIMITED_ACCESS' | 'UNDERAGE' | undefined

type variablesArrayType = Array<string>

interface VerifyUserResponse {
    userType: UserType
    isRedirect: boolean
}

interface URLParams {
    autoLoginParamHash: string
    fapParam?: string
}

export const useLoginHook = () => {
    const { isFeatureEnabled } = useRemoteConfig()
    const location = useLocation()
    const { state, operations } = useLoginContext()
    const { dispatchLoginSuccess } = useLoginSuccess()
    const { updatePatientConfig, ...service } = useLoginService()
    const isWebview = !!window?.ReactNativeWebView
    const { executeRecaptcha } = useGoogleReCaptcha()
    const { redirectToUrlExternalWithUniqueToken } = useRedirectExternalUniqueToken()
    const { verifyRedirectUrl, newRedirectFlow } = useRedirectTo()
    const { trackEvent } = useTracking()

    const { getUserCIPFromBase, getUserFromBase, getUserFeatureFlags } = useUserService()

    const { booleans } = defaultConfig

    const updateBooleanFeatureFlags = useCallback(
        async (variablesKeys: variablesArrayType, featureFlagValues: Array<{ key: string; value: string | number | boolean }>) => {
            if (featureFlagValues.length) {
                const activatedVariables = variablesKeys.reduce((resultValue, currentValue: keyof typeof booleans) => {
                    const isDisabledFlag = featureFlagValues.find(flag => flag.key === currentValue && !flag.value)

                    if (isDisabledFlag && isFeatureEnabled(currentValue)) {
                        return [
                            ...resultValue,
                            {
                                key: currentValue,
                                value: isFeatureEnabled(currentValue),
                            },
                        ]
                    }

                    return resultValue
                }, [])

                await Promise.all(activatedVariables.map(variable => updatePatientConfig(variable.key, variable.value)))
            }
        },
        [booleans, isFeatureEnabled, updatePatientConfig],
    )

    const getViewerFeatureFlags = useCallback(async (): Promise<ViewerFeatureFlags[] | undefined> => {
        const user = await getUserFeatureFlags()
        return user.featureFlags
    }, [getUserFeatureFlags])

    const activateFeatureFlags = useCallback(async () => {
        const featureFlags = await getViewerFeatureFlags()

        const flagsToUpdate = ['HEALTH_MAP_ELIGIBILITY_V1']

        try {
            featureFlags && (await updateBooleanFeatureFlags(flagsToUpdate, featureFlags))
        } catch (e) {
            console.error('error on activateFlag')
        }
    }, [getViewerFeatureFlags, updateBooleanFeatureFlags])

    const checkUserType = (user: User): UserType => {
        if (user.underage) {
            return 'UNDERAGE'
        }
        if (user.limitedFlow) {
            return 'LIMITED_ACCESS'
        }

        return 'FULL'
    }

    const reportsHistory = () => {
        if (isFeatureEnabled('AUTH_LOAD_REPORTS_HISTORY')) {
            service.reportsHistory()
        }
    }

    const loadUser = useCallback(async (): Promise<{ userType: UserType | undefined; user: User | undefined }> => {
        const lsAuthData = operations.getAuthData()
        let userType: UserType | undefined
        let userResponse

        if (lsAuthData?.token) {
            if (lsAuthData?.limitedFlow && !lsAuthData?.refreshToken) {
                userResponse = await getUserCIPFromBase()
            } else {
                userResponse = await getUserFromBase()
                reportsHistory()
            }
            operations.addPayloadLS(lsAuthData, lsAuthData?.limitedFlow || userResponse.underage || userResponse.limitedFlow)

            userType = checkUserType(userResponse)
            operations.setData({
                token: lsAuthData.token,
                refreshToken: lsAuthData.refreshToken,
                signed: !!lsAuthData.token,
                user: userResponse,
                limitedFlow: lsAuthData.limitedFlow || userType !== 'FULL',
            })
        }
        return { user: userResponse, userType }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [operations, getUserCIPFromBase, getUserFromBase])

    const verifyUser = useCallback(
        async (
            analyticsData: {
                loginType?: string
                externalStep?: 'LOGIN' | 'PHASED_LOGIN' | string
                cip?: string
                birthDate?: string
                rememberMe?: boolean
            } = {},
        ): Promise<VerifyUserResponse> => {
            const lsAuthData = operations.getAuthData()
            const { userType, user } = await loadUser()
            const urlWithParams = sessionStorage.getItem(SESSION_STORAGES_KEYS.REDIRECT_TO_URL)
            const redirectToUrl = sessionStorage.getItem('@LiviaAuth:Campaigns')
            const isUniqueToken = sessionStorage.getItem(SESSION_STORAGES_KEYS.UNIQUE_TOKEN)
            const isUniqueAccess = sessionStorage.getItem(SESSION_STORAGES_KEYS.UNIQUE_ACCESS)
            const isRedirect = userType === 'LIMITED_ACCESS' || userType === 'UNDERAGE' || !!redirectToUrl
            user && dispatchLoginSuccess(user, analyticsData)
            user && trackEvent({ event: 'Reset_Senha_Concluido' })

            // LIMITED_ACCESS Redirect
            if (userType === 'LIMITED_ACCESS' || userType === 'UNDERAGE') {
                verifyRedirectUrl({ redirectToUrl: defaultConfig.strings.AUTH_REDIRECT_LOGIN_LIMITED_FLOW })

                sessionStorage.removeItem(SESSION_STORAGES_KEYS.UNIQUE_TOKEN)
                sessionStorage.removeItem(SESSION_STORAGES_KEYS.REDIRECT_TO_URL)

                return {
                    userType,
                    isRedirect,
                }
            }

            // Unique Token redirect
            const uniqueTokenRedirect = lsAuthData?.token && lsAuthData?.refreshToken && redirectToUrl && isUniqueToken

            if (uniqueTokenRedirect) {
                await redirectToUrlExternalWithUniqueToken({
                    accessToken: lsAuthData.token!,
                    refreshToken: lsAuthData.refreshToken!,
                    redirectToUrl,
                    uniqueAccess: isUniqueAccess === 'true',
                })
            }

            await activateFeatureFlags()

            if (!uniqueTokenRedirect && redirectToUrl) {
                const params = urlWithParams ? removePath(urlWithParams) : undefined

                if (params) {
                    verifyRedirectUrl({ redirectToUrl, redirectParams: params })
                }

                if (!params) {
                    verifyRedirectUrl({ redirectToUrl })
                }

                sessionStorage.removeItem(SESSION_STORAGES_KEYS.REDIRECT_TO_URL)
            }

            return {
                userType,
                isRedirect,
            }
        },
        [
            operations,
            loadUser,
            dispatchLoginSuccess,
            trackEvent,
            activateFeatureFlags,
            verifyRedirectUrl,
            redirectToUrlExternalWithUniqueToken,
        ],
    )

    const loadData = useCallback(async () => {
        try {
            await loadUser()
        } catch (error) {
            operations.emptyData()
            operations.clearStorage()
        }
    }, [loadUser, operations])

    const checkToken = useCallback(
        async (token: string) => {
            if (!isTokenExpired(token)) {
                await loadData()
                return
            }

            operations.emptyData()
            operations.clearStorage()
        },
        [loadData, operations],
    )

    const authenticate = async (credentials: SignInCredentials): Promise<void> => {
        const data = await service.authenticate(credentials)
        if (data) {
            operations.addPayloadTokenLS({
                token: data.accessToken,
                refreshToken: data.refreshToken,
                limitedFlow: true,
                redesign: undefined,
            })
            operations.setIdentifier(LoginType.empty)
        }
    }

    const authenticateWithHash = async (
        params: URLParams,
        { setLoading }: { setLoading: (value: boolean) => void },
    ): Promise<void> => {
        try {
            const data = await service.authenticateWithHash(params.autoLoginParamHash)
            const { accessToken, refreshToken = '', type } = data || {}

            if (accessToken) {
                operations.addPayloadTokenLS({
                    token: accessToken,
                    limitedFlow: type !== 'SINGLE_LOGIN',
                    refreshToken: refreshToken,
                    redesign: false,
                })

                await verifyUser({
                    externalStep: 'LOGIN',
                    loginType: tracking.LOGIN_TYPE.AuthenticateHash,
                })

                let redirectTo: string = type !== 'SINGLE_LOGIN' ? '/plataforma/exames/acesso-rapido' : '/plataforma/exames'
                if (params.fapParam) {
                    redirectTo = `${redirectTo}?fap=${params.fapParam}`
                }
                goToPage(redirectTo)
            }
        } catch (err) {
            goToPage(ROUTES_PATH.SIGN_IN)
        } finally {
            setLoading(false)
        }
    }

    const authenticateWithUniqueToken = async (uniqueToken: string, redirectTo?: string): Promise<void> => {
        try {
            const data = await service.authenticateWithUniqueToken(uniqueToken)
            const { accessToken, refreshToken } = data || {}

            const redirectToUrl = refreshToken
                ? redirectTo || defaultConfig.strings.AUTH_REDIRECT_LOGIN
                : defaultConfig.strings.AUTH_REDIRECT_LOGIN_LIMITED_FLOW

            if (accessToken) {
                operations.addPayloadTokenLS({
                    token: accessToken,
                    limitedFlow: !refreshToken,
                    refreshToken: refreshToken || '',
                })

                await verifyUser({
                    externalStep: 'LOGIN',
                    loginType: tracking.LOGIN_TYPE.AuthenticateUniqueToken,
                })

                if (!newRedirectFlow) {
                    return goToPage(redirectToUrl)
                }

                verifyRedirectUrl({
                    redirectToUrl,
                })
            }
        } catch (err) {
            singleSPA.historyPush(ROUTES_PATH.NEW_SESSION_EXPIRED)
        }
    }

    const authenticateCIP = async (credentials: SignInCIPCredentials): Promise<void> => {
        const data = await service.authenticateCIP(credentials)

        if (data) {
            operations.addPayloadTokenLS({ token: data.accessToken, refreshToken: '', limitedFlow: true, redesign: false })
            operations.setIdentifier(LoginType.empty)
        }
    }

    const getLabs = async () => {
        if (state.labs.length === 0) {
            const data = await service.getLabs()
            operations.setLabs(data)
        }
    }

    useEffect(() => {
        if (!state.authState.user) {
            if (location.pathname.includes('/external')) {
                return
            }

            if (isWebview) {
                loadData()
                return
            }

            if (state.authState.refreshToken) {
                checkToken(state.authState.refreshToken)
                return
            }

            // Verificacao para usuarios CIP_ORPHAN (CIP_ORPHAN não tem refreshToken, somente token)
            if (state.authState.limitedFlow && state.authState.token) {
                checkToken(state.authState.token)
                return
            }
        }
    }, [operations, state.authState, isWebview, loadData, checkToken, location.pathname])

    const resetAuthState = async () => {
        operations.setData(EMPTY_DATA_USER)
    }

    const getRecaptchaToken = async (): Promise<string | null> => {
        if (!executeRecaptcha) {
            return null
        }

        return await executeRecaptcha('login')
    }

    return {
        ...state,
        getLabs,
        loadUser,
        loadData,
        authenticate,
        resetAuthState,
        authenticateCIP,
        getRecaptchaToken,
        authenticateWithHash,
        authenticateWithUniqueToken,
        signOut: operations.signOut,
        setSubmitting: operations.setSubmitting,
        setIdentifier: operations.setIdentifier,
        verifyUser,
    }
}
